'use client'
/*
 * SPDX-FileCopyrightText: 2026 The HedgeDoc developers (see AUTHORS file)
 *
 * SPDX-License-Identifier: AGPL-3.0-only
 */
import type { PageSettings } from './page-settings-modal'
import { PageSettingsModal } from './page-settings-modal'
import type { PropsWithChildren } from 'react'
import React, { createContext, useCallback, useContext, useMemo, useState } from 'react'

export interface NewPageModalPrefill {
  path?: string
}

type OpenNewPageModal = (prefill?: NewPageModalPrefill) => void

const NewPageModalContext = createContext<OpenNewPageModal | undefined>(undefined)

const emptySettings: PageSettings = { title: '', path: '', badge: '' }

/**
 * Provides a function to open the modal for creating a new page and renders the modal.
 *
 * @param children The children that can open the modal
 */
export const NewPageModalProvider: React.FC<PropsWithChildren> = ({ children }) => {
  const [show, setShow] = useState(false)
  const [initialSettings, setInitialSettings] = useState<PageSettings>(emptySettings)

  const openNewPageModal = useCallback<OpenNewPageModal>((prefill) => {
    setInitialSettings({ ...emptySettings, path: prefill?.path ?? '' })
    setShow(true)
  }, [])
  const hideModal = useCallback(() => setShow(false), [])

  const modal = useMemo(
    () => <PageSettingsModal show={show} onHide={hideModal} mode={'create'} initialSettings={initialSettings} />,
    [show, hideModal, initialSettings]
  )

  return (
    <NewPageModalContext.Provider value={openNewPageModal}>
      {children}
      {modal}
    </NewPageModalContext.Provider>
  )
}

/**
 * Returns a function that opens the modal for creating a new page, optionally with a prefilled path.
 *
 * @throws Error if used outside of a {@link NewPageModalProvider}
 */
export const useOpenNewPageModal = (): OpenNewPageModal => {
  const openNewPageModal = useContext(NewPageModalContext)
  if (openNewPageModal === undefined) {
    throw new Error('useOpenNewPageModal must be used inside a NewPageModalProvider')
  }
  return openNewPageModal
}
